import React, { createRef, useState, useMemo, useEffect } from 'react'
import {
    useQuery,
    useQueryClient,
    QueryClient,
    QueryClientProvider,
  } from 'react-query'

import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { WidgetInput, Address } from 'react-addressfinder';

import { map_styles, map_stylesOverlay } from './google-map-style';
import Prose from '@ui-kit/atoms/prose'
import Spinner from '@ui-kit/atoms/spinner'

import './style.css'

type CoverageMapOptionsConfig = {
    heading: string,
    body: string,
    env: string
}

const queryClient = new QueryClient()

const mapCentre = { lat: -40.9, lng: 174.886 }
const defaultGeneration = '4G';

const CoverageMap: React.FC<CoverageMapOptionsConfig> = ({ heading, body, env = process.env.GATSBY_SITE, ...props }) => {

    return (
        <div className='promotion coverage-map'>
            <div className="promotion__container">
                <figure className="promotion__map">
                    <QueryClientProvider client={queryClient}>
                        <Map />
                    </QueryClientProvider>
                </figure>
                <div className="promotion__body">
                    {heading ? <h2 >{heading}</h2> : null}
                    {body && <Prose className="prose" env={env} html={body} wysiwygFiles={[]} />}
                </div>
            </div>
            <span  id="coverage" />
        </div>
    )
}
export default CoverageMap


const Map = () => {
    const layerID = '2dCoverage';
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY
    })
    const [map, setMap] = useState(null)
    const [ filters, setFilters ] = useState({
        keywords: '',
        generation: defaultGeneration
    })
    const [ marker, setMarker ] = useState(null);


    const onUnmount = React.useCallback(function callback(map) {
        setMap(null)
    }, [])

    const { data, isLoading, isError } = useQuery('coverage-map', async () => {
        return await fetch(`/coverage-map-tiles.json`).then(async res => await res.json());
    })

    const onLoad = React.useCallback(function callback(map) {
        applyCoverage(map, data, defaultGeneration);
        setMap(map)
      }, [data, filters.generation])

    const cachedMutatedData = useMemo(() => {
        if (isLoading || isError || !map) return null

        applyCoverage(map, data, filters.generation);
        return data
      }, [isLoading, isError, data, filters.generation])

    if(isLoading) {
        return <Spinner label={'Loading'} />
    }

    return (
        <div className="coverage-map__wrapper">
            <div className="coverage-map__filters">
                <div className="coverage-map__keywords">
                    <label htmlFor="coverage-map__address">Enter your address to check the coverage at your place</label>
                    {map && (
            <WidgetInput
                addressFinderKey={process.env.GATSBY_ADDRESS_FINDER_KEY}
                //inputClassName={fieldClassName}
                id={'coverage-map__address'}
                name={'coverage-map__address'}
                placeholder={'Search...'}
                country='NZ'
                raw={true}
                onSelected={(fullAddress: string, address: Address) => {
                    const position = {
                        lat: parseFloat(address.y),
                        lng: parseFloat(address.x)
                    }

                    setMarker(<Marker position={position} title={fullAddress} />)

                    const bounds = new window.google.maps.LatLngBounds(position);
                    map.fitBounds(bounds);
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  /* make sure a valid address was selected from the list */
                }}
            />)}
            </div>
            <ul className="coverage-map__generation">
                {data.map(tech => (
                    <li key={tech.label}>
                        <button type="button" disabled={filters.generation === tech.label} onClick={() => setFilters({...filters, generation: tech.label })}>{tech.label}</button>
                    </li>
                ))}
            </ul>
            </div>
            {/*JSON.stringify(data)*/}
            {isLoaded ? (
                <GoogleMap
                mapContainerStyle={{
                    width: '100%',
                    height: '620px'
                  }}
                center={mapCentre}
                zoom={5}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    maxZoom: 14,
                    //zoomControl: false,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    backgroundColor: `hsla(0, 0%, 0%, 0)`,
                    styles: map_styles
                }}
              >
                {marker}
                <></>
              </GoogleMap>
            ) : null}
            <footer>
                <ul>
                    <li><span style={{backgroundColor: "#4c84c5"}} />Excellent</li>
                    <li><span style={{backgroundColor: "#55a8d8"}} />Good</li>
                    <li><span style={{backgroundColor: "#82c5e5"}} />Fair</li>
                    <li><span style={{backgroundColor: "#f0f0f0"}} />No signal</li>
                </ul>
            <p>Signal strength shown is indicative. Actual coverage may vary based on device and other environmental factors.</p>
            </footer>
        </div>
    )
}


function applyCoverage(map, data, gen) {

    const tile = data.find(({ label }) => label === gen);
    const tileUrl = `${tile.relPath}{z}/{x}/{y}.png`;

    const styledMapType = new google.maps.StyledMapType(map_stylesOverlay, { name: ' Nova Styled Map' });

    const layer = new window.google.maps.ImageMapType({
        name: gen,
        getTileUrl(coord, zoom) {
          const url = tileUrl
            .replace('{x}', coord.x.toString())
            .replace('{y}', coord.y.toString())
            .replace('{z}', zoom.toString());
          return url;
        },
        tileSize: new window.google.maps.Size(256, 256),
        minZoom: 1,
        maxZoom: 20,
        opacity: 0.7,

    });


    map.overlayMapTypes.clear();
    map.overlayMapTypes.push(layer);
    map.overlayMapTypes.push(styledMapType);

    if(tile.circles) {

    }

    // mutate data here
}
